import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  SimpleGrid,
  Select,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import {
  cocreateArticle,
  fetchArticleTemplates,
  fetchCampaigns,
  fetchMyPrincipal,
  updateArticle,
} from '../services/api';

const DEFAULT_TEMPLATES = [
  {
    slug: 'professional',
    displayName: 'Professional',
    description: 'Restrained serif, navy accents - credible and corporate',
  },
  {
    slug: 'modern',
    displayName: 'Modern',
    description: 'Tight sans headline, pill accents, generous white space - product-y',
  },
  {
    slug: 'creative',
    displayName: 'Creative',
    description: 'Warm tones, playful type and colour pops - personality-forward',
  },
];

function TemplateMini({ slug, coachName }) {
  const accents = {
    professional: { bg: '#f8fafc', ink: '#1d2733', mark: '#155a76', shape: 'underline' },
    modern: { bg: '#f5faf8', ink: '#101614', mark: '#2f7a68', shape: 'pill' },
    creative: { bg: '#faf4ec', ink: '#2b2118', mark: '#c0492b', shape: 'dashed' },
  };
  const theme = accents[slug] || accents.professional;
  return (
    <Box
      style={{
        minHeight: 128,
        borderRadius: 10,
        background: theme.bg,
        border: '1px solid rgba(0,0,0,0.06)',
        padding: 12,
      }}
    >
      <Text size="xs" fw={700} style={{ color: theme.mark, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        The One-Person Unicorn
      </Text>
      {theme.shape === 'underline' && (
        <Box style={{ width: 44, height: 2, borderRadius: 999, background: theme.mark, marginTop: 8, marginBottom: 10 }} />
      )}
      {theme.shape === 'pill' && (
        <Box style={{ display: 'inline-block', marginTop: 8, marginBottom: 10, padding: '2px 8px', borderRadius: 999, background: '#dff0e9', color: theme.mark, fontSize: 10 }}>
          Article
        </Box>
      )}
      {theme.shape === 'dashed' && (
        <Box style={{ marginTop: 8, marginBottom: 10, borderTop: `2px dashed ${theme.mark}` }} />
      )}
      <Text size="xs" style={{ color: theme.ink, lineHeight: 1.45 }}>
        Three practical ideas to upgrade your leadership signal.
      </Text>
      <Text size="xs" style={{ color: '#5f6c68', marginTop: 6, lineHeight: 1.45 }}>
        Built for busy founders and operators.
      </Text>
      <Text size="xs" style={{ color: '#5f6c68', marginTop: 6 }}>
        By {coachName || 'Sample Coach'}
      </Text>
    </Box>
  );
}

export default function AviaryV2ArticleNewCocreatePage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const back = location.state?.back || { path: '/v2/articles/new', label: 'Add' };
  const defaultRole = location.state?.defaultRole || 'promo';

  const [topic, setTopic] = useState('');
  const [notes, setNotes] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [templateSlug, setTemplateSlug] = useState('professional');
  const [principalName, setPrincipalName] = useState('Sample Coach');
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchCampaigns(api).catch(() => []),
      fetchArticleTemplates(api).catch(() => DEFAULT_TEMPLATES),
      fetchMyPrincipal(api).catch(() => null),
    ]).then(([campaignData, templateData, principal]) => {
      if (cancelled) return;
      const list = Array.isArray(campaignData)
        ? campaignData
        : (Array.isArray(campaignData?.campaigns) ? campaignData.campaigns : []);
      setCampaigns(list);
      const nextTemplates = Array.isArray(templateData) && templateData.length ? templateData : DEFAULT_TEMPLATES;
      setTemplates(nextTemplates);
      const selectedExists = nextTemplates.some((item) => item.slug === templateSlug);
      if (!selectedExists) setTemplateSlug('professional');
      const fallbackName = String(principal?.displayName || principal?.name || '').trim();
      if (fallbackName) setPrincipalName(fallbackName);
      setLoadingCampaigns(false);
    }).catch(() => {
      if (cancelled) return;
      setCampaigns([]);
      setTemplates(DEFAULT_TEMPLATES);
      setLoadingCampaigns(false);
    });
    return () => { cancelled = true; };
  }, [api]);

  const campaignSelectData = useMemo(() => ([
    { value: '', label: 'No specific campaign' },
    ...campaigns.map((campaign) => ({
      value: String(campaign.id),
      label: String(campaign.name || campaign.id),
    })),
  ]), [campaigns]);

  const canSubmit = topic.trim().length >= 10 && !submitting;

  async function handleGenerate() {
    const trimmedTopic = topic.trim();
    if (trimmedTopic.length < 10 || submitting) return;
    setSubmitting(true);
    try {
      const response = await cocreateArticle(api, {
        topic: trimmedTopic,
        notes: notes.trim(),
        campaignId: campaignId || '',
        templateSlug,
      });
      const slug = String(response?.article?.slug || '').trim();
      if (!slug) throw new Error('Article slug missing from response');
      if (defaultRole === 'context') {
        await updateArticle(api, slug, { articleRole: 'context' });
      }
      const nextPath = defaultRole === 'context'
        ? '/v2/knowledge-base'
        : `/v2/articles/${encodeURIComponent(slug)}/cowrite`;
      navigate(nextPath, {
        state: { back: { path: '/v2/articles/new/cocreate', label: 'Cocreate' }, defaultRole },
      });
    } catch (err) {
      notifications.show({
        title: 'Could not generate draft',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
      setSubmitting(false);
    }
  }

  return (
    <Box style={{ padding: '32px 24px 120px', maxWidth: 760, margin: '0 auto' }}>
      <Stack gap="lg">
        <UnstyledButton
          onClick={() => navigate(back.path)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--aviary-muted)',
            fontSize: 13,
            width: 'fit-content',
          }}
        >
          <IconArrowLeft size={14} /> Back to {back.label}
        </UnstyledButton>

        <Stack gap={4}>
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
            Cocreate with Aviary AI
          </Text>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            Start with a topic and optional notes. Aviary writes a full first draft you can edit and publish.
          </Text>
        </Stack>

        <Paper p="lg" radius="md" withBorder style={{ position: 'relative' }}>
          <LoadingOverlay visible={submitting} zIndex={10} overlayProps={{ radius: 'md', blur: 1 }} />
          <Stack gap="md">
            <Textarea
              label="What's this article about?"
              placeholder="e.g. Founder to operator transitions"
              value={topic}
              onChange={(e) => setTopic(e.currentTarget.value)}
              autosize
              minRows={3}
              maxRows={8}
              disabled={submitting}
              required
            />
            <Textarea
              label="Any notes, angle, or examples? (optional)"
              placeholder="Specific audience, examples, points to emphasize, etc."
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              autosize
              minRows={4}
              maxRows={10}
              disabled={submitting}
            />
            <Select
              label="For which campaign? (optional)"
              data={campaignSelectData}
              value={campaignId}
              onChange={(value) => setCampaignId(value || '')}
              searchable
              disabled={submitting || loadingCampaigns}
            />
            <Stack gap={6}>
              <Text size="sm" fw={600} style={{ color: 'var(--aviary-ink)' }}>Pick a look</Text>
              <Text size="xs" c="dimmed">
                your draft gets wrapped in this template - switchable later in detail view
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                {templates.map((template) => {
                  const selected = template.slug === templateSlug;
                  return (
                    <Paper
                      key={template.slug}
                      role="button"
                      tabIndex={0}
                      withBorder
                      onClick={() => setTemplateSlug(template.slug)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') setTemplateSlug(template.slug);
                      }}
                      style={{
                        minHeight: 240,
                        padding: 10,
                        border: selected
                          ? '2px solid var(--mantine-color-sage-6)'
                          : '1px solid var(--aviary-disabled)',
                        cursor: 'pointer',
                        position: 'relative',
                      }}
                    >
                      {selected && (
                        <Badge
                          radius="xl"
                          color="sage"
                          variant="filled"
                          leftSection={<IconCheck size={12} />}
                          style={{ position: 'absolute', right: 10, top: 10 }}
                        >
                          Selected
                        </Badge>
                      )}
                      <TemplateMini slug={template.slug} coachName={principalName} />
                      <Text mt="sm" size="sm" fw={700} style={{ color: 'var(--aviary-ink)' }}>
                        {template.displayName}
                      </Text>
                      <Text size="xs" c="dimmed" style={{ lineHeight: 1.35 }}>
                        {template.description}
                      </Text>
                    </Paper>
                  );
                })}
              </SimpleGrid>
              <Text size="xs" c="dimmed">
                Preview byline uses {principalName}.
              </Text>
            </Stack>
            <Group justify="space-between" align="center">
              <Text size="xs" c="dimmed">
                {topic.trim().length < 10
                  ? 'Topic must be at least 10 characters.'
                  : `Generates a draft article in the ${(templates.find((item) => item.slug === templateSlug)?.displayName || 'Professional')} look and opens it in detail view.`}
              </Text>
              <Button color="sage" onClick={handleGenerate} disabled={!canSubmit} loading={submitting}>
                Generate draft →
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
